/**
 * Investigator API service.
 * Routes through the Operations Agent (new) at /api/operations-agent.
 */
import { API_ENDPOINTS } from '../config/api';
import { getAuthHeaders } from '../utils/apiClient';
import { logAndFallback } from '../utils/userFacingError';
import { browserTimezone } from '../utils/briefPresentation';
import type {
  InvestigationRequest,
  InvestigationResponse,
  InvestigatorHealth,
  InvestigatorCapabilities,
  InvestigationProgressEvent,
  AgentSummary,
} from '../types/investigator';

// Prefer relative API calls so Vite proxy / same-origin works in dev/prod.
// Fallback to configured Zeecore base for deployments that run truth-layer separately.
const ZEECORE = API_ENDPOINTS.base || '';
const OPS_AGENT = `${ZEECORE}/api/operations-agent`;

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  return await Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
    }),
  ]);
}

/** Local Ollama + tool loop on broad triage questions can exceed 4 min. */
const INVESTIGATION_TIMEOUT_MS = 600_000;

function formatInvestigatorFetchError(err: unknown, timeoutMs: number): string {
  if (err instanceof DOMException && err.name === 'AbortError') {
    const mins = Math.round(timeoutMs / 60_000);
    return (
      `The investigation timed out after ${mins} minutes. ` +
      'Broad questions can take longer — try a more specific question about an order, vehicle, or customer.'
    );
  }
  if (err instanceof Error && /aborted without reason/i.test(err.message)) {
    const mins = Math.round(timeoutMs / 60_000);
    return (
      `The investigation was cancelled after about ${mins} minutes. ` +
      'Try a more specific question, then ask again.'
    );
  }
  if (err instanceof TypeError && /failed to fetch/i.test(err.message)) {
    return (
      'Could not reach ZOE right now. Please try again in a moment. ' +
      'If this keeps happening, contact your ZeeFleet administrator.'
    );
  }
  return logAndFallback(
    err,
    '[ZOE] investigator request error:',
    'Something went wrong with this investigation. Please try again, or contact your ZeeFleet administrator.',
  );
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = 12_000): Promise<Response> {
  const controller = new AbortController();
  const t = setTimeout(
    () => controller.abort(new DOMException(`Investigation timed out after ${timeoutMs}ms`, 'AbortError')),
    timeoutMs,
  );
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (err) {
    throw new Error(formatInvestigatorFetchError(err, timeoutMs));
  } finally {
    clearTimeout(t);
  }
}

export async function submitInvestigation(
  request: InvestigationRequest,
): Promise<InvestigationResponse> {
  let headers: HeadersInit;
  try {
    // If Cognito session refresh hangs, we'd previously never reach fetch(). Time-box it.
    headers = await withTimeout(getAuthHeaders(), 6_000, 'Auth header retrieval');
  } catch (err) {
    // Fall back to unauthenticated headers so we can still show a useful backend error.
    headers = { 'Content-Type': 'application/json' };
  }

  // Investigator queries may take a while (tool execution + LLM). Use a longer timeout than health checks.
  const res = await fetchWithTimeout(
    `${OPS_AGENT}/query`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(withClientTimezone(request)),
    },
    INVESTIGATION_TIMEOUT_MS,
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(body || `Investigation failed (${res.status})`);
  }
  const json = (await res.json()) as InvestigationResponse;

  // Lightweight "last result" tracking for the Agents landing page (best-effort).
  try {
    localStorage.setItem('agents_last_result_investigator_ts', new Date().toISOString());
    localStorage.setItem('agents_last_result_investigator_intent', String(json.intent || ''));
    // Count anomaly evidence items when present.
    const anomalies = Array.isArray(json.evidence)
      ? json.evidence.filter((e) => String(e.evidence_type || '').startsWith('anomaly_')).length
      : 0;
    localStorage.setItem('agents_last_result_investigator_anomaly_count', String(anomalies));
    if (json.summary) localStorage.setItem('agents_last_result_investigator_summary', String(json.summary).slice(0, 280));
  } catch {
    // ignore
  }

  return json;
}

export async function fetchInvestigatorHealth(): Promise<InvestigatorHealth> {
  let headers: HeadersInit;
  try {
    headers = await withTimeout(getAuthHeaders(), 6_000, 'Auth header retrieval');
  } catch {
    headers = { 'Content-Type': 'application/json' };
  }
  const res = await fetchWithTimeout(`${OPS_AGENT}/health`, { headers });
  if (!res.ok) throw new Error(`Health check failed (${res.status})`);
  return res.json();
}

/** GET /agents — loaded manifest summaries (I0-E7), used to label the thread
 *  UI's per-turn agent badge (I1-E6) from a turn's `agent_id`. */
export async function fetchAgents(): Promise<AgentSummary[]> {
  let headers: HeadersInit;
  try {
    headers = await withTimeout(getAuthHeaders(), 6_000, 'Auth header retrieval');
  } catch {
    headers = { 'Content-Type': 'application/json' };
  }
  const res = await fetchWithTimeout(`${OPS_AGENT}/agents`, { headers });
  if (!res.ok) throw new Error(`Agent list fetch failed (${res.status})`);
  const json = (await res.json()) as { agents: AgentSummary[] };
  return json.agents ?? [];
}

export async function fetchInvestigatorCapabilities(): Promise<InvestigatorCapabilities> {
  let headers: HeadersInit;
  try {
    headers = await withTimeout(getAuthHeaders(), 6_000, 'Auth header retrieval');
  } catch {
    headers = { 'Content-Type': 'application/json' };
  }
  const res = await fetchWithTimeout(`${OPS_AGENT}/capabilities`, { headers });
  if (!res.ok) throw new Error(`Capabilities check failed (${res.status})`);
  return res.json();
}

/**
 * Stream a live investigation via SSE (POST /query/stream).
 *
 * Calls `onProgress` for each `InvestigationProgressEvent` received during
 * the ReAct loop, then resolves the returned Promise with the final
 * `InvestigationResponse` when the `result` SSE event arrives.
 *
 * The caller must supply an `AbortSignal` to cancel the stream when the
 * component unmounts or the user submits a new question.
 */
/** Zoe schedules any brief it drafts in the user's own zone; the server validates it. */
function withClientTimezone(request: InvestigationRequest): InvestigationRequest {
  return request.clientTimezone ? request : { ...request, clientTimezone: browserTimezone() };
}

export async function streamInvestigation(
  request: InvestigationRequest,
  onProgress: (event: InvestigationProgressEvent) => void,
  signal?: AbortSignal,
): Promise<InvestigationResponse> {
  let headers: HeadersInit;
  try {
    headers = await withTimeout(getAuthHeaders(), 6_000, 'Auth header retrieval');
  } catch {
    headers = { 'Content-Type': 'application/json' };
  }

  const res = await fetch(`${OPS_AGENT}/query/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(withClientTimezone(request)),
    signal,
  }).catch((err) => {
    throw new Error(formatInvestigatorFetchError(err, INVESTIGATION_TIMEOUT_MS));
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(body || `Investigation stream failed (${res.status})`);
  }

  if (!res.body) {
    throw new Error('No response body for SSE stream');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // Parse the SSE wire format. Each event block is separated by \n\n.
  // Each line may be "event: <name>" or "data: <json>".
  const parseBlock = (block: string): { eventName: string; data: string } | null => {
    let eventName = 'message';
    let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) eventName = line.slice(7).trim();
      else if (line.startsWith('data: ')) data = line.slice(6);
    }
    return data ? { eventName, data } : null;
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Process all complete blocks (terminated by \n\n).
    let boundary: number;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      const parsed = parseBlock(block);
      if (!parsed) continue;

      if (parsed.eventName === 'progress') {
        try {
          const event = JSON.parse(parsed.data) as InvestigationProgressEvent;
          onProgress(event);
        } catch {
          // ignore malformed progress events — don't fail the whole stream
        }
      } else if (parsed.eventName === 'result') {
        const json = JSON.parse(parsed.data) as InvestigationResponse;
        // Persist best-effort analytics (same as submitInvestigation).
        try {
          localStorage.setItem('agents_last_result_investigator_ts', new Date().toISOString());
          localStorage.setItem('agents_last_result_investigator_intent', String(json.intent || ''));
          const anomalies = Array.isArray(json.evidence)
            ? json.evidence.filter((e) => String(e.evidence_type || '').startsWith('anomaly_')).length
            : 0;
          localStorage.setItem('agents_last_result_investigator_anomaly_count', String(anomalies));
          if (json.summary) localStorage.setItem('agents_last_result_investigator_summary', String(json.summary).slice(0, 280));
        } catch { /* ignore */ }
        return json;
      } else if (parsed.eventName === 'error') {
        const err = JSON.parse(parsed.data) as { message: string };
        throw new Error(err.message || 'Investigation stream error');
      }
    }
  }

  throw new Error('SSE stream ended without a result event');
}
